import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Star, MessageSquare, X, Check, FileText, Globe, BadgeCheck, Wrench } from "lucide-react";
import { absUrl, api } from "@/lib/api";
import { listingPath, categoryPath } from "@/lib/paths";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useCurrency } from "@/contexts/CurrencyContext";
import { toast } from "sonner";
import QuickRFQModal from "@/components/QuickRFQModal";
import ViewBadge from "@/components/ViewBadge";

// Read the visitor's geo from sessionStorage (populated by CategoryPage on first mount).
function useBuyerGeo() {
  return useMemo(() => {
    try {
      const raw = sessionStorage.getItem("as_geoip");
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  }, []);
}

// True when the listing's regions cover the buyer's geo (global, matching country_code, or matching state_code).
function shipsToBuyer(listing, geo) {
  if (!geo || !geo.country_code) return false;
  if (listing.global) return true;
  const buyerCountry = (geo.country_code || "").toUpperCase();
  const buyerState   = (geo.region_code || "").toUpperCase();
  const listingCode  = (listing.country_code || "").toUpperCase();
  // Multi-country array (iter31)
  const multi = Array.isArray(listing.countries) ? listing.countries : [];
  for (const c of multi) {
    if ((c.code || "").toUpperCase() !== buyerCountry) continue;
    if (c.nationwide) return true;
    const states = (c.states || []).map((s) => (s || "").toUpperCase());
    if (states.length === 0 || (buyerState && states.includes(buyerState))) return true;
    if (!buyerState) return true;
  }
  // Legacy flat fields
  if (listingCode && listingCode === buyerCountry) {
    if (listing.nationwide) return true;
    const states = (listing.state_codes || []).map((s) => (s || "").toUpperCase());
    if (states.length === 0) return true; // country match, no state restriction
    if (buyerState && states.includes(buyerState)) return true;
  }
  return false;
}

function RequestInfoModal({ open, onClose, listing }) {
  const [form, setForm] = useState({ customer_name: "", customer_email: "", hotel: "", message: "" });
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/request-info", { listing_id: listing.id, ...form });
      if (data.email_status === "sent") toast.success("Info request sent to supplier");
      else toast.success("Info request recorded");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  const supplierName = listing.company || listing.advertiser_name || "Supplier";

  return (
    <div className="fixed inset-0 z-50 bg-[#11211A]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="request-info-modal">
      <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8ECE9]">
          <div>
            <div className="tracking-overline mb-1">Request Quote</div>
            <div className="font-serif text-lg text-[#11211A]">{listing.title}</div>
            <div className="text-xs text-[#7D8C85] mt-0.5">by {supplierName}</div>
          </div>
          <button type="button" onClick={onClose} className="text-[#7D8C85] hover:text-[#11211A]" data-testid="req-info-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <div className="bg-[#F0EFEA] text-xs text-[#4A5550] p-3">
            <div><span className="text-[#7D8C85]">Item:</span> <span className="text-[#11211A]">{listing.title}</span></div>
            <div><span className="text-[#7D8C85]">Supplier:</span> <span className="text-[#11211A]">{supplierName}</span></div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Your name *</label>
            <input required value={form.customer_name} onChange={set("customer_name")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="req-info-name" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Hotel / Property</label>
            <input value={form.hotel} onChange={set("hotel")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="req-info-hotel" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Email *</label>
            <input required type="email" value={form.customer_email} onChange={set("customer_email")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="req-info-email" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Message *</label>
            <textarea required rows={4} value={form.message} onChange={set("message")} placeholder="What do you want to know about this product?" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="req-info-message" />
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50" data-testid="req-info-submit">
              {busy ? "Sending…" : "Send request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CustomOemBadge({ item }) {
  if (!item.custom_oem) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 bg-[#FBF1E8] text-[#B35C4A] border border-[#B35C4A]/30" title="Offers custom-made or OEM / private-label products" data-testid={`custom-oem-badge-${item.id}`}>
      <Wrench className="w-2.5 h-2.5" /> Custom / OEM
    </span>
  );
}

function SubcategoryTags({ item }) {
  const navigate = useNavigate();
  const subs = Array.isArray(item.subcategories) ? item.subcategories.filter((s) => s && s.name) : [];
  if (subs.length === 0) return null;
  const shown = subs.slice(0, 3);
  const extra = subs.length - shown.length;
  // Rendered inside the card's profile <Link> on paid cards, so these are not nested anchors — they navigate on click.
  const go = (to) => (e) => { e.preventDefault(); e.stopPropagation(); navigate(to); };
  const tagCls = "text-[10px] leading-tight px-1.5 py-0.5 bg-[#F0EFEA] text-[#4A5550] border border-[#E8ECE9] hover:border-[#264839] hover:text-[#264839] transition-colors cursor-pointer";
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid={`subcategory-tags-${item.id}`} title={subs.map((s) => s.name).join(" · ")}>
      {shown.map((s) => (
        <span key={s.slug} role="link" tabIndex={0} onClick={go(categoryPath(s))} onKeyDown={(e) => { if (e.key === "Enter") go(categoryPath(s))(e); }} className={tagCls} title={`See all ${s.name} suppliers`} data-testid={`subcategory-tag-${item.id}-${s.slug}`}>{s.name}</span>
      ))}
      {extra > 0 && (
        <span role="link" tabIndex={0} onClick={go(listingPath(item))} onKeyDown={(e) => { if (e.key === "Enter") go(listingPath(item))(e); }} className="text-[10px] text-[#7D8C85] hover:text-[#264839] underline underline-offset-2 cursor-pointer" title="See everything this supplier sells" data-testid={`subcategory-tags-more-${item.id}`}>+{extra} more</span>
      )}
    </div>
  );
}

function FreeListingCard({ item }) {
  const company = item.company || item.advertiser_name || item.title || "Supplier";
  const region = [item.country || item.country_code].filter(Boolean).join(", ");
  return (
    <div className="border border-dashed border-[#C4CFC9] bg-[#FAFAF7] flex flex-col relative" data-testid={`listing-card-${item.id}`}>
      <div className="p-4 flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] uppercase tracking-[0.2em] font-semibold text-[#7D8C85] bg-[#E8ECE9] px-2 py-0.5" data-testid={`free-listing-badge-${item.id}`}>Free Listing</span>
          {item.is_unclaimed && (
            <span className="text-[9px] uppercase tracking-[0.15em] text-[#B35C4A]" data-testid={`unclaimed-badge-${item.id}`}>Unclaimed</span>
          )}
        </div>
        <div className="flex items-start gap-3">
          {item.logo_url && (
            <div className="w-12 h-12 shrink-0 bg-white border border-[#E8ECE9] flex items-center justify-center" data-testid={`free-logo-${item.id}`}>
              <img src={absUrl(item.logo_url)} alt={`${company} logo`} className="max-w-full max-h-full object-contain" />
            </div>
          )}
          <div className="min-w-0">
            <Link to={listingPath(item)} className="block group">
              <h3 className="font-serif text-lg text-[#11211A] leading-snug group-hover:text-[#264839] transition-colors inline-flex items-center gap-2 flex-wrap">
                {company}
                {item.is_verified && <VerifiedBadge size="sm" testId={`verified-check-${item.id}`} />}
              </h3>
            </Link>
            <SubcategoryTags item={item} />
            {item.custom_oem && <div className="mt-1.5"><CustomOemBadge item={item} /></div>}
            {region && (
              <div className="flex items-center gap-1 text-[11px] text-[#7D8C85] mt-1" data-testid={`free-region-${item.id}`}>
                <MapPin className="w-3 h-3" /> {region}
              </div>
            )}
          </div>
        </div>
        {item.description?.trim() ? (
          <p className="text-xs text-[#4A5550] mt-2 line-clamp-3">{item.description}</p>
        ) : (
          <p className="text-xs text-[#9AA6A0] italic mt-2" data-testid={`free-description-placeholder-${item.id}`}>Description coming soon</p>
        )}
      </div>
      <div className="px-4 pb-4 flex flex-col gap-2">
        <Link to={listingPath(item)} className="text-xs uppercase tracking-[0.15em] py-2 border border-[#C4CFC9] text-[#4A5550] hover:border-[#264839] transition-colors flex items-center justify-center gap-2" data-testid={`view-profile-btn-${item.id}`}>
          View profile
        </Link>
        {item.is_unclaimed && (
          <Link
            to={`${listingPath(item)}?claim=1`}
            className="text-[11px] text-[#7D8C85] hover:text-[#B35C4A] underline underline-offset-2 text-center transition-colors"
            data-testid={`claim-listing-card-btn-${item.id}`}
          >
            Is this your company? Claim this listing
          </Link>
        )}
      </div>
    </div>
  );
}

export default function ListingCard({ item, spotlight = false, onSpotlightClick }) {
  const [reqOpen, setReqOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const { formatRange } = useCurrency();
  const priceLabel = formatRange(item.price_min, item.price_max, item.price);
  const geo = useBuyerGeo();
  const ships = shipsToBuyer(item, geo);

  if (!item.tier || item.tier === "free") return <FreeListingCard item={item} />;

  // Fresh Picks: a verified Founding Supplier has earned its spot — brand-green frame + "Verified & Featured" instead of the generic banner.
  const earned = spotlight && item.founding_supplier && (item.is_verified || item.is_unclaimed === false);

  return (
    <>
      <div className={`card-listing block group flex flex-col relative ${earned ? "border-2 border-[#264839] shadow-[0_10px_30px_-12px_rgba(38,72,57,0.45)]" : ""}`} data-testid={`listing-card-${item.id}`} data-earned={earned ? "true" : undefined}>
        {earned && (
          <div className="absolute top-3 left-3 z-10" data-testid={`verified-featured-badge-${item.id}`}>
            <div className="bg-[#264839] text-white text-[10px] uppercase tracking-[0.18em] font-semibold px-3 py-1 shadow-md flex items-center gap-1.5" title="Verified supplier, featured for consistent buyer engagement">
              <BadgeCheck className="w-3.5 h-3.5" /> Verified &amp; Featured
            </div>
          </div>
        )}
        {!earned && item.founding_supplier && (
          <div
            className="absolute top-3 -left-1 z-10 group/badge"
            data-testid={`founding-badge-${item.id}`}
          >
            <div
              className="bg-gradient-to-r from-[#B8860B] via-[#D4A017] to-[#B8860B] text-white text-[10px] uppercase tracking-[0.18em] font-semibold px-3 py-1 shadow-md flex items-center gap-1 cursor-help"
              style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 8px) 50%, 100% 100%, 0 100%)" }}
              title="Founding Supplier — hand-vetted from our first 50 launch partners. Prioritised in RFQ delivery and homepage rotation."
            >
              <Star className="w-3 h-3 fill-current" /> Founding Supplier
            </div>
            {/* Rich hover tooltip — appears on desktop hover only */}
            <div className="hidden group-hover/badge:block absolute top-full left-0 mt-1.5 z-20 w-64 p-3 bg-[#11211A] text-white text-[11px] leading-relaxed shadow-lg rounded-sm" role="tooltip" data-testid={`founding-tooltip-${item.id}`}>
              <div className="font-semibold text-[#D4A017] mb-1 flex items-center gap-1"><Star className="w-3 h-3 fill-current" /> Founding Supplier</div>
              <p>Hand-vetted from our first 50 launch partners. Receives priority RFQ delivery, homepage rotation and locked-in launch pricing for 12 months.</p>
            </div>
          </div>
        )}
        {/* Iter58 · 50-day featured ribbon. Only visible while founding_featured_until is in the future.
            Iter79 · Public copy is "Featured Leader" — the expiry date is internal only and stays on the tooltip. */}
        {item.founding_featured && item.founding_featured_until && new Date(item.founding_featured_until) > new Date() && (
          <div
            className="absolute top-3 right-3 z-10"
            data-testid={`founding-pinned-ribbon-${item.id}`}
          >
            <div
              className="bg-[#264839] text-[#F0C866] text-[10px] uppercase tracking-[0.18em] font-semibold px-3 py-1 shadow-md border border-[#F0C866]/40 flex items-center gap-1"
              title={`Pinned to the top of the category through ${item.founding_featured_until.split("T")[0]}`}
            >
              <Star className="w-3 h-3 fill-current" />
              Featured Leader
            </div>
          </div>
        )}
        <Link to={listingPath(item)} className="block" onClick={() => onSpotlightClick?.()} data-testid={spotlight ? `spotlight-link-${item.id}` : undefined}>
          <div className="img-wrap aspect-[4/3] bg-[#E8ECE9]">
            {item.image_url ? (
              <img src={absUrl(item.image_url)} alt={item.title} className="w-full h-full object-cover" />
            ) : item.logo_url ? (
              <div className="w-full h-full bg-white flex items-center justify-center p-6" data-testid={`card-logo-fallback-${item.id}`}>
                <img src={absUrl(item.logo_url)} alt={`${item.company || item.advertiser_name || item.title} logo`} className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#C4CFC9] font-serif text-2xl">No image</div>
            )}
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              {item.is_category_leader ? (
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white bg-[#B35C4A] px-2 py-0.5 flex items-center gap-1" data-testid={`leader-badge-${item.id}`}>
                  <Star className="w-3 h-3 fill-current" /> Featured Leader
                </span>
              ) : item.is_featured ? (
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#B35C4A] flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" /> Featured
                </span>
              ) : <span />}
              {priceLabel ? <span className="text-sm font-medium text-[#11211A]" data-testid={`price-${item.id}`}>{priceLabel}</span> : null}
            </div>
            <h3 className="font-serif text-lg text-[#11211A] leading-snug group-hover:text-[#264839] transition-colors">
              {item.title}
            </h3>
            <div className="text-[11px] text-[#7D8C85] mt-1 inline-flex items-center gap-1 flex-wrap" data-testid={`supplier-name-${item.id}`}>
              <span>by <span className="text-[#11211A] font-medium">{item.company || item.advertiser_name || "Supplier"}</span></span>
              {item.is_verified && <VerifiedBadge size="sm" testId={`verified-check-${item.id}`} />}
            </div>
            <SubcategoryTags item={item} />
            {/* Type + Region badges */}
            {(item.supplier_type || item.custom_oem || (item.state_codes && item.state_codes.length > 0) || item.country_code || item.global || ships) && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`badges-${item.id}`}>
                <CustomOemBadge item={item} />
                {item.global && (
                  <span
                    className="text-[9px] uppercase tracking-[0.14em] font-semibold px-1.5 py-0.5 bg-[#264839] text-white border border-[#264839] inline-flex items-center gap-1"
                    data-testid={`global-badge-${item.id}`}
                    title="Ships worldwide — this supplier accepts orders from any country"
                  >
                    <Globe className="w-2.5 h-2.5" /> Global Supplier
                  </span>
                )}
                {ships && !item.global && (
                  <span
                    className="text-[9px] uppercase tracking-[0.14em] font-semibold px-1.5 py-0.5 bg-green-600 text-white border border-green-700 inline-flex items-center gap-1"
                    data-testid={`ships-to-you-${item.id}`}
                    title={`Ships to your location${geo?.region_code ? ` (${geo.region_code})` : geo?.country_code ? ` (${geo.country_code})` : ""}`}
                  >
                    <Check className="w-2.5 h-2.5" /> Ships to you
                  </span>
                )}
                {item.supplier_type && (
                  <span
                    className="text-[9px] uppercase tracking-[0.14em] font-semibold px-1.5 py-0.5 bg-[#264839]/8 text-[#264839] border border-[#264839]/25"
                    data-testid={`type-badge-${item.id}`}
                    title={`${item.supplier_type} — ${item.supplier_type === "Manufacturer" ? "makes the product in-house" : "resells at wholesale volumes"}`}
                  >
                    {item.supplier_type}
                  </span>
                )}
                {(item.state_codes || []).slice(0, 2).map((code) => (
                  <span
                    key={code}
                    className="text-[9px] uppercase tracking-[0.14em] font-semibold px-1.5 py-0.5 bg-[#B35C4A]/8 text-[#B35C4A] border border-[#B35C4A]/25"
                    data-testid={`region-badge-${item.id}-${code}`}
                  >
                    {code}
                  </span>
                ))}
                {(!item.state_codes || item.state_codes.length === 0) && item.country_code && (
                  <span
                    className="text-[9px] uppercase tracking-[0.14em] font-semibold px-1.5 py-0.5 bg-[#B35C4A]/8 text-[#B35C4A] border border-[#B35C4A]/25"
                    data-testid={`region-badge-${item.id}-${item.country_code}`}
                  >
                    Nationwide · {item.country_code}
                  </span>
                )}
              </div>
            )}
            <p className="text-xs text-[#7D8C85] mt-2 line-clamp-2">{item.description}</p>
            {item.views_this_month >= 10 && (
              <div className="mt-2"><ViewBadge views={item.views_this_month} listingId={item.id} /></div>
            )}
            {/* Iter58: Response-time badge (default until 3+ replies, then avg from users.avg_response_hours) */}
            {(() => {
              const count = item.rfq_response_count || 0;
              const hasEnough = count >= 3 && typeof item.avg_response_hours === "number";
              const label = hasEnough
                ? `Typically responds in ${item.avg_response_hours < 1 ? "under 1 hr" : item.avg_response_hours < 24 ? `~${Math.round(item.avg_response_hours)}hr${Math.round(item.avg_response_hours) === 1 ? "" : "s"}` : `~${Math.round(item.avg_response_hours / 24)}d`}`
                : "Typically responds within 24hrs";
              return (
                <div
                  className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-medium text-[#264839] bg-emerald-50 border border-emerald-200 px-1.5 py-0.5"
                  data-testid={`response-time-badge-${item.id}`}
                  title={hasEnough ? `Based on the supplier's last ${count} RFQ replies` : "Default reply time until this supplier has 3+ tracked RFQ responses"}
                >
                  <span className="inline-block w-1 h-1 rounded-full bg-emerald-500"></span>
                  {label}
                </div>
              );
            })()}
            {priceLabel && (
              <div className="text-[10px] text-[#7D8C85] mt-2 italic">Price varies by order volume and specifications.</div>
            )}
            {(() => {
              // Global suppliers get a prominent "Ships to: Anywhere (Global)" label instead of country/state
              if (item.global) {
                return (
                  <div className="flex items-center gap-1.5 text-xs font-medium text-[#264839] mt-3 bg-[#264839]/8 border border-[#264839]/25 px-2 py-1 rounded-sm" data-testid={`ships-to-anywhere-${item.id}`}>
                    <Globe className="w-3.5 h-3.5" />
                    <span>Ships to: <strong>Anywhere (Global)</strong></span>
                  </div>
                );
              }
              const states = (item.state_codes || []).filter(Boolean);
              const parts = [item.city, states.join(" / "), item.country].filter(Boolean);
              const nationwide = !parts.length && item.country_code;
              if (!parts.length && !nationwide) return null;
              return (
                <div className="flex items-center gap-1 text-xs text-[#4A5550] mt-3">
                  <MapPin className="w-3 h-3" />
                  Ships to: {nationwide ? `Nationwide${item.country_code ? ` — ${item.country_code}` : ""}` : parts.join(", ")}
                </div>
              );
            })()}
          </div>
        </Link>
        <div className="px-4 pb-4 mt-auto flex gap-2">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickOpen(true); }}
            className="flex-1 text-xs uppercase tracking-[0.15em] py-2 bg-[#264839] text-white hover:bg-[#1a3227] transition-colors flex items-center justify-center gap-2"
            data-testid={`quick-quote-btn-${item.id}`}
          >
            <FileText className="w-3.5 h-3.5" /> Quick Quote
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReqOpen(true); }}
            className="text-xs uppercase tracking-[0.15em] py-2 px-3 border border-[#264839] text-[#264839] hover:bg-[#264839] hover:text-white transition-colors flex items-center justify-center gap-2"
            data-testid={`request-info-btn-${item.id}`}
            title="Send a general enquiry to this supplier"
          >
            <MessageSquare className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <RequestInfoModal open={reqOpen} onClose={() => setReqOpen(false)} listing={item} />
      <QuickRFQModal open={quickOpen} onClose={() => setQuickOpen(false)} listing={item} />
    </>
  );
}
